import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import brandGuideService from '../../services/brandGuideService';
import { IBrandGuideSection } from '../../types/damTypes';

// Fontana brand colors
const COLORS = {
  olympia: '#1B3967',
  originalet: '#0050E6',
  sand: '#EDE8DC',
  sandLight: '#F5F0E8',
  sandDark: '#D4CFC3',
  white: '#FFFFFF',
  text: '#333333',
};

// PDF hämtas nu via API

interface BrandGuideLandingPageProps {
  onClose: () => void;
  onOpenSection: (slug: string) => void;
  canEdit?: boolean;
  onOpenAdmin?: () => void;
  onOpenPptPolicies?: () => void;
}

interface BrandGuideSettingsData {
  headerSubtitle: string;
  headerTitle: string;
  headerDescription: string;
  footerText: string;
}

const DEFAULT_SETTINGS: BrandGuideSettingsData = {
  headerSubtitle: 'FONTANA • EST. 1978',
  headerTitle: 'Grafisk Manual',
  headerDescription: 'En godare värld för alla. Här samlar vi riktlinjer för hur Fontanas varumärke kommuniceras visuellt – från logotyp och färger till tonalitet och bildspråk.',
  footerText: 'Fontana är ett svenskt familjeföretag grundat sedan 1978. Vi är smaksökare som är trygga i vår mattradition men nyfikna på andras.',
};

const BrandGuideLandingPage: React.FC<BrandGuideLandingPageProps> = ({
  onClose,
  onOpenSection,
  canEdit = false,
  onOpenAdmin,
  onOpenPptPolicies,
}) => {
  const [sections, setSections] = useState<IBrandGuideSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [settings, setSettings] = useState<BrandGuideSettingsData>(DEFAULT_SETTINGS);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    
    // Hämta både sektioner och inställningar
    Promise.all([
      brandGuideService.fetchSections(!canEdit),
      brandGuideService.getSettings().catch(() => DEFAULT_SETTINGS),
    ])
      .then(([sectionsData, settingsData]) => {
        if (mounted) {
          setSections(sectionsData);
          setSettings(settingsData);
          setError(null);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.response?.data?.message || err.message || 'Ett fel inträffade.');
        }
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [canEdit]);

  useEffect(() => {
    let cancelled = false;
    // Kolla om PDF finns via API
    brandGuideService.getPdfInfo()
      .then((info) => {
        if (!cancelled) {
          setPdfAvailable(info.exists);
          if (info.exists && info.url) {
            setPdfUrl(info.url);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setPdfAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedSections = useMemo(
    () =>
      [...sections].sort(
        (a, b) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
          a.title.localeCompare(b.title)
      ),
    [sections]
  );

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: `linear-gradient(180deg, ${COLORS.sandLight} 0%, ${COLORS.white} 100%)`,
        position: 'relative',
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10,
          bgcolor: COLORS.white,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': { bgcolor: COLORS.sandLight },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Hero Section */}
      <Box
        sx={{
          background: COLORS.olympia,
          color: COLORS.white,
          py: { xs: 6, md: 8 },
          px: { xs: 3, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            left: '30%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.02)',
          }}
        />

        <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 4,
              fontSize: '0.75rem',
              opacity: 0.7,
              mb: 1,
              display: 'block',
            }}
          >
            {settings.headerSubtitle}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Kapra Neue", "Oswald", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '4rem' },
              lineHeight: 1,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              mb: 3,
            }}
          >
            {settings.headerTitle}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1.25rem' },
              maxWidth: 600,
              lineHeight: 1.6,
              opacity: 0.9,
              mb: 4,
            }}
          >
            {settings.headerDescription}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {pdfAvailable && pdfUrl && (
              <Button
                component="a"
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  bgcolor: COLORS.white,
                  color: COLORS.olympia,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': {
                    bgcolor: COLORS.sandLight,
                  },
                }}
              >
                Ladda ner komplett PDF
              </Button>
            )}
            {onOpenPptPolicies && (
              <Button
                onClick={onOpenPptPolicies}
                variant="contained"
                startIcon={<SlideshowIcon />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: COLORS.white,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  backdropFilter: 'blur(4px)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.25)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  },
                }}
              >
                PPT Policys
              </Button>
            )}
            {canEdit && onOpenAdmin && (
              <Button
                onClick={onOpenAdmin}
                variant="outlined"
                startIcon={<SettingsIcon />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: COLORS.white,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': {
                    borderColor: COLORS.white,
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Hantera sektioner
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.olympia }} />
          </Box>
        ) : !sortedSections.length ? (
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
            Inga sektioner har publicerats ännu.
            {canEdit && ' Använd "Hantera sektioner" för att skapa den första.'}
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: { xs: 2, md: 3 },
            }}
          >
            {sortedSections.map((section) => {
              // Använd anpassade färger från databasen eller fallback
              const gradientStart = section.cardGradientStart || COLORS.olympia;
              const gradientEnd = section.cardGradientEnd || COLORS.originalet;
              const cardSymbol = section.cardSymbol || '◆';
              const cardImage = section.cardImage;
              const isHovered = hoveredCard === section._id;

              return (
                <Box
                  key={section._id}
                  onClick={() => onOpenSection(section.slug)}
                  onMouseEnter={() => setHoveredCard(section._id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: COLORS.white,
                    boxShadow: isHovered
                      ? '0 20px 40px rgba(27, 57, 103, 0.15)'
                      : '0 4px 20px rgba(0, 0, 0, 0.06)',
                    transform: isHovered ? 'translateY(-4px)' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Card Header with Visual */}
                  <Box
                    sx={{
                      height: 140,
                      background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Decorative pattern */}
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0.1,
                        backgroundImage: `radial-gradient(circle at 20% 80%, white 1px, transparent 1px),
                                         radial-gradient(circle at 80% 20%, white 1px, transparent 1px)`,
                        backgroundSize: '30px 30px',
                      }}
                    />
                    {cardImage ? (
                      <Box
                        component="img"
                        src={cardImage}
                        alt={section.title}
                        sx={{
                          maxWidth: '70%',
                          maxHeight: '70%',
                          objectFit: 'contain',
                          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: cardSymbol.length > 2 ? '2.5rem' : '4rem',
                          fontWeight: 700,
                          color: COLORS.white,
                          opacity: 0.9,
                          fontFamily: '"Kapra Neue", "Oswald", sans-serif',
                          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                          transition: 'transform 0.3s ease',
                        }}
                      >
                        {cardSymbol}
                      </Typography>
                    )}
                  </Box>

                  {/* Card Content */}
                  <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      sx={{
                        fontFamily: '"Oswald", sans-serif',
                        fontWeight: 600,
                        fontSize: '1.4rem',
                        color: COLORS.olympia,
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        mb: 1.5,
                      }}
                    >
                      {section.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: COLORS.text,
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        flexGrow: 1,
                        opacity: 0.8,
                      }}
                    >
                      {section.previewText}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 2,
                        color: COLORS.olympia,
                        fontWeight: 600,
                        fontSize: '0.9rem',
                      }}
                    >
                      <span>Läs mer</span>
                      <ArrowForwardIcon
                        sx={{
                          fontSize: 18,
                          transform: isHovered ? 'translateX(4px)' : 'none',
                          transition: 'transform 0.2s ease',
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: COLORS.sand,
          py: 4,
          px: { xs: 2, md: 4 },
          mt: 4,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center' }}>
          <Typography
            sx={{
              color: COLORS.olympia,
              fontSize: '0.9rem',
              opacity: 0.7,
            }}
          >
            {settings.footerText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default BrandGuideLandingPage;
